import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = ["India", "UAE", "Thailand", "Vietnam", "Indonesia", "Malaysia", "Singapore", "Other"];
const BIZ_TYPES = ["DMC", "Tour Operator", "Travel Agency", "Destination Specialist", "Other"];
const DEST_COUNTS = ["1–3", "4–10", "11–25", "25+"];
const VOLUMES = ["Under 20", "20–50", "50–200", "200+"];
const NEEDS = ["Website", "Package Management", "Inquiry Management", "Quotation Engine", "Complete Platform"];

const INITIAL = {
  business_name: "", country: "", business_type: "", current_website: "",
  destinations_count: "", monthly_inquiries: "", needs: [], name: "", email: "", phone: "",
};

const inputCls =
  "w-full rounded-xl border border-sand bg-white px-4 py-3.5 text-base text-ink outline-none transition-colors duration-300 placeholder:text-stone2/50 focus:border-terra";

const ChoiceGrid = ({ options, value, onChange, tidPrefix }) => (
  <div className="grid gap-2.5 sm:grid-cols-2">
    {options.map((o) => (
      <button
        key={o}
        type="button"
        onClick={() => onChange(o)}
        data-testid={`${tidPrefix}-${o.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
        className={`rounded-xl border px-4 py-3.5 text-left text-sm font-medium transition-all duration-300 ${
          value === o ? "border-terra bg-terra text-ink" : "border-sand bg-white text-ink hover:border-terra/50"
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

export default function LeadForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setError(""); };

  const STEPS = [
    {
      title: "What's your business name?",
      valid: () => form.business_name.trim().length > 1,
      content: (
        <input
          value={form.business_name}
          onChange={(e) => set("business_name", e.target.value)}
          placeholder="e.g. Arabian Horizons DMC"
          data-testid="demo-form-step-1-input-business-name"
          className={inputCls}
          autoFocus
        />
      ),
    },
    {
      title: "Which country do you operate in?",
      valid: () => !!form.country,
      content: (
        <select
          value={form.country}
          onChange={(e) => set("country", e.target.value)}
          data-testid="demo-form-step-2-select-country"
          className={inputCls}
        >
          <option value="" disabled>Select your primary market</option>
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      ),
    },
    {
      title: "What type of travel business are you?",
      valid: () => !!form.business_type,
      content: <ChoiceGrid options={BIZ_TYPES} value={form.business_type} onChange={(v) => set("business_type", v)} tidPrefix="demo-form-step-3-option" />,
    },
    {
      title: "Do you have a current website?",
      valid: () => true,
      content: (
        <input
          value={form.current_website}
          onChange={(e) => set("current_website", e.target.value)}
          placeholder="https:// (optional)"
          data-testid="demo-form-step-4-input-website"
          className={inputCls}
        />
      ),
    },
    {
      title: "How many destinations do you cover?",
      valid: () => !!form.destinations_count,
      content: <ChoiceGrid options={DEST_COUNTS} value={form.destinations_count} onChange={(v) => set("destinations_count", v)} tidPrefix="demo-form-step-5-option" />,
    },
    {
      title: "Roughly how many inquiries do you handle monthly?",
      valid: () => !!form.monthly_inquiries,
      content: <ChoiceGrid options={VOLUMES} value={form.monthly_inquiries} onChange={(v) => set("monthly_inquiries", v)} tidPrefix="demo-form-step-6-option" />,
    },
    {
      title: "What are you looking for?",
      valid: () => form.needs.length > 0,
      content: (
        <div className="grid gap-2.5 sm:grid-cols-2">
          {NEEDS.map((n) => {
            const on = form.needs.includes(n);
            return (
              <button
                key={n}
                type="button"
                onClick={() => set("needs", on ? form.needs.filter((x) => x !== n) : [...form.needs, n])}
                data-testid={`demo-form-step-7-need-${n.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center justify-between rounded-xl border px-4 py-3.5 text-left text-sm font-medium transition-all duration-300 ${
                  on ? "border-terra bg-terra text-ink" : "border-sand bg-white text-ink hover:border-terra/50"
                }`}
              >
                {n}
                {on && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      ),
    },
    {
      title: "Where should we reach you?",
      valid: () => form.name.trim().length > 1 && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length > 4,
      content: (
        <div className="space-y-2.5">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" data-testid="demo-form-step-8-input-name" className={inputCls} />
          <input value={form.email} onChange={(e) => set("email", e.target.value)} type="email" placeholder="Email" data-testid="demo-form-step-8-input-email" className={inputCls} />
          <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="WhatsApp number" data-testid="demo-form-step-8-input-phone" className={inputCls} />
        </div>
      ),
    },
  ];

  const current = STEPS[step];
  const next = () => {
    if (!current.valid()) { setError("Please complete this step to continue."); return; }
    setError("");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    if (!current.valid()) { setError("Please add your name, a valid email and WhatsApp number."); return; }
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="demo" className="border-y border-sand bg-parch py-24 lg:py-32">
      <div className="container-x grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal><Chapter index={11} label="Request a Demo" /></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-8 font-serif text-3xl leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Let's Build Your DMC's Digital Platform.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-stone2">
              Tell us about your destination business. We'll come back with a demo
              that shows Travel-Tek wearing your brand.
            </p>
            <div className="mt-10 hidden gap-1.5 lg:flex">
              {STEPS.map((_, i) => (
                <span key={i} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step || done ? "bg-terra" : "bg-sand"}`} />
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.12} className="lg:col-span-7">
          <div className="rounded-2xl border border-sand bg-white p-7 shadow-[0_35px_70px_-35px_rgba(11,31,51,0.3)] sm:p-10" data-testid="demo-lead-form">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex min-h-[320px] flex-col items-center justify-center text-center"
                data-testid="demo-form-success"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald2/10 text-emerald2">
                  <Check className="h-8 w-8" />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-ink sm:text-3xl">Request received.</h3>
                <p className="mt-3 max-w-sm text-base text-stone2">
                  Thanks. We'll be in touch to understand your business and show you Travel-Tek.
                </p>
              </motion.div>
            ) : (
              <>
                <div className="mb-8 flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone2" data-testid="demo-form-step-indicator">
                    Step {step + 1} of {STEPS.length}
                  </span>
                  <div className="flex gap-1 lg:hidden">
                    {STEPS.map((_, i) => (
                      <span key={i} className={`h-1 w-4 rounded-full ${i <= step ? "bg-terra" : "bg-sand"}`} />
                    ))}
                  </div>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <h3 className="mb-6 font-serif text-2xl text-ink sm:text-3xl">{current.title}</h3>
                    {current.content}
                  </motion.div>
                </AnimatePresence>
                {error && <p className="mt-4 text-sm font-medium text-terra" data-testid="demo-form-error">{error}</p>}
                <div className="mt-8 flex items-center justify-between">
                  <button
                    onClick={back}
                    disabled={step === 0}
                    data-testid="demo-form-prev-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-sand px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-ink disabled:opacity-30"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                  {step < STEPS.length - 1 ? (
                    <button
                      onClick={next}
                      data-testid="demo-form-next-btn"
                      className="group inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-terra hover:text-ink"
                    >
                      Continue <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  ) : (
                    <button
                      onClick={submit}
                      disabled={sending}
                      data-testid="demo-form-submit-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-terra px-6 py-3 text-sm font-bold text-ink transition-colors duration-300 hover:bg-ink hover:text-paper disabled:opacity-60"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Request a Demo
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
